import React from 'react';
import * as api from '../../api';
import { createSearchParams, Link, useNavigate } from "react-router-dom";
import styled from 'styled-components'
import dayjs from 'dayjs';
import CurrentInscriptionsSection from './CurrentInscriptionsSection'; // Import the new component

import { supabase } from '../../lib/supabaseClient';

const PageHeader = styled.div`
    h1 {
        color: #2c3e50;
        font-size: 32px;
        font-weight: 700;
        margin-bottom: 8px;
        position: relative;
        display: inline-block;
    }
`;

const CreateEventButtonWrapper = styled.div`
    text-align: center;
    margin: 30px 0;

    a {
        text-decoration: none;
    }
`;

const CreateEventButton = styled.button`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 14px 32px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
    }

    &:active {
        transform: translateY(0);
    }
`;

const SectionTitle = styled.h3`
    color: #34495e;
    font-size: 24px;
    font-weight: 600;
    margin: 40px 0 20px 0;
    display: flex;
    align-items: center;

    &:before {
        content: '${props => props.icon || '📋'}';
        margin-right: 12px;
        font-size: 28px;
    }
`;

const EventsSection = styled.div`
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    margin-bottom: 24px;
`;

const EventLineContainer = styled.div`
    display: flex;
    align-items: center;
    padding: 16px;
    margin: 8px 0;
    background: ${props => props.isActive ? 'linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%)' : '#ffffff'};
    border: 1px solid ${props => props.isActive ? '#3498db20' : '#e1e8ed'};
    border-radius: 10px;
    transition: all 0.3s ease;

    &:hover {
        transform: translateX(4px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        border-color: #3498db;
    }

    & > div:first-child {
        flex: 2;
        padding-right: 16px;

        strong {
            color: #2c3e50;
            font-size: 16px;
            font-weight: 600;
            display: block;
            margin-bottom: 4px;
        }

        .thin {
            color: #7f8c8d;
            font-size: 13px;
            font-weight: 400;
        }
    }

    & > span {
        flex: 1;
        text-align: center;
        padding: 0 8px;
    }

    .button-column {
        flex: 1.5;
        display: flex;
        gap: 8px;
        justify-content: flex-end;
    }
`;

const StatusBadge = styled.span`
    display: inline-block;
    padding: 6px 14px;
    font-size: 12px;
    color: ${props => {
        switch(props.status) {
            case 'À venir': return '#f39c12';
            case 'Inscriptions en cours': return '#27ae60';
            case 'Inscriptions terminées': return '#e74c3c';
            case 'Terminé': return '#95a5a6';
            default: return '#95a5a6';
        }
    }};
`;

const EditButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    margin-top: 8px;
    background: linear-gradient(135deg, #3498db, #2980b9);
    color: white;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    justify-content: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(52, 152, 219, 0.3);
    }

    .crayon {
        font-size: 16px;
    }
`;

const PressButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    margin-top: 8px;
    margin-left: 8px;
    background: linear-gradient(135deg, #9b59b6, #8e44ad);
    color: white;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    justify-content: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(155, 89, 182, 0.3);
    }

    span {
        font-size: 16px;
    }
`;

const SuiviButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    margin-top: 8px;
    margin-left: 8px;
    background: linear-gradient(135deg, #27ae60, #229954);
    color: white;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    justify-content: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(39, 174, 96, 0.3);
    }

    span {
        font-size: 16px;
    }
`;

const PartenairesButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    margin-top: 8px;
    margin-left: 8px;
    background: linear-gradient(135deg, #3498db, #2980b9);
    color: white;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    justify-content: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(52, 152, 219, 0.3);
    }

    span {
        font-size: 16px;
    }
`;

const ExpoButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    margin-top: 8px;
    margin-left: 8px;
    background: linear-gradient(135deg, #e67e22, #d35400);
    color: white;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    justify-content: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(230, 126, 34, 0.3);
    }

    span {
        font-size: 16px;
    }
`;

const SelectionFinaleButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    margin-top: 8px;
    margin-left: 8px;
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    justify-content: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(243, 156, 18, 0.3);
    }

    span {
        font-size: 16px;
    }
`;

const ActionButton = styled.button`
    padding: 8px 16px;
    background: ${props => props.primary ?
        'linear-gradient(135deg, #9b59b6, #8e44ad)' :
        'linear-gradient(135deg, #ecf0f1, #bdc3c7)'};
    color: ${props => props.primary ? 'white' : '#2c3e50'};
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    }
`;

const EmptyStateMessage = styled.div`
    text-align: center;
    padding: 40px;
    color: #95a5a6;
    font-size: 16px;
    font-style: italic;

    &:before {
        content: '📭';
        display: block;
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.5;
    }
`;

const EventsAdminPanel = (props) => {

    const navigate = useNavigate();
    const [speedatings, setSpeedatings] = React.useState([]);
    const [fd100s, setFd100s] = React.useState([]);
    const [designEvents, setDesignEvents] = React.useState([]);
    const [allEvents, setAllEvents] = React.useState([]); // New state to store all events

    React.useEffect(() => {
        getEvents();
    }, []);

    async function getEvents() {
        try {
            // Fetch from MongoDB (existing)
            const mongoResponse = await api.getEvents();
            const mongoEvents = mongoResponse.data;
            console.log('MongoDB events:', mongoEvents);

            // Fetch from Supabase
            const { data: supabaseEvents, error } = await supabase
                .from('events')
                .select('*');

            if (error) {
                console.error('Error fetching Supabase events:', error);
            } else {
                console.log('Supabase events:', supabaseEvents);
            }

            // Combine events from both sources
            // MongoDB events have _id, Supabase events have id
            // Add a source field to distinguish them
            const combinedEvents = [
                ...mongoEvents.map(event => ({ ...event, source: 'mongodb' })),
                ...(supabaseEvents || []).map(event => ({
                    ...event,
                    _id: event.id, // Map Supabase id to _id for consistency
                    source: 'supabase'
                }))
            ];

            setAllEvents(combinedEvents); // Store all combined events
            setSpeedatings(combinedEvents.filter(event => event.type === "sde" || event.type === "sdo"));
            setFd100s(combinedEvents.filter(event => event.type === "fd100"));
            setDesignEvents(combinedEvents.filter(event => event.type === "design"));

            console.log('Combined events:', combinedEvents);
        } catch (error) {
            console.error('Error fetching events:', error);
        }
    }

    function EventLine(props){

        function getEventStatus(event){
            if(new Date(event.inscriptions_start_date) > new Date()){
                return "À venir"
            }
            if(new Date(event.inscriptions_end_date).setHours(23, 59, 59) > new Date()){
                return "Inscriptions en cours"
            }
            if(new Date(event.main_date) > new Date()){
                return "Inscriptions terminées"
            }
            return "Terminé"
        }

        const status = getEventStatus(props.event);
        const isActive = status === "Inscriptions en cours";

        const isSupabaseSDE = props.event.source === 'supabase' && (props.event.type === 'sde' || props.event.type === 'sdo');

        return (
            <EventLineContainer isActive={isActive}>
                <div>
                    <strong>{props.event.title_fr}</strong>
                    <div className='thin'>{new dayjs(props.event.main_date || props.event.event_start_date).format('dddd D MMMM YYYY')}</div>
                    {isSupabaseSDE ? (
                        <>
                        <EditButton onClick={()=>navigate({pathname:"/admin/events/createevent-modern",search:createSearchParams({edit:props.event._id}).toString()})}>
                            Modifier <span className='crayon'>✎</span>
                        </EditButton>
                        <EditButton style={{ marginLeft: 8 }} onClick={()=>navigate({pathname:"/admin/events/sde",search:createSearchParams({eid:props.event._id}).toString()})}>
                            Dashboard <span className='crayon'>📊</span>
                        </EditButton>
                        </>
                    ) : (
                        <EditButton onClick={()=>{
                            const isMilan2026 = props.event.type === "design" &&
                                               (props.event.title_fr?.toLowerCase().includes('milan 2026') ||
                                                props.event.title_fr?.toLowerCase().includes('milan2026'));

                            if (isMilan2026) {
                                navigate({pathname:"/admin/events/design-details",search:createSearchParams({id:props.event._id}).toString()});
                            } else {
                                navigate({pathname:"editevent",search:createSearchParams({id:props.event._id}).toString()});
                            }
                        }}>
                            {props.event.type === "design" && (props.event.title_fr?.toLowerCase().includes('milan 2026') || props.event.title_fr?.toLowerCase().includes('milan2026')) ? "Détails" : "Modifier"} <span className='crayon'>✎</span>
                        </EditButton>
                    )}
                </div>
                <StatusBadge status={status}>{status}</StatusBadge>
                <div className='button-column'>
                    {isSupabaseSDE ? (
                        <>
                            <ActionButton primary onClick={()=>navigate({pathname:"/admin/events/sde/shortlist",search:createSearchParams({eid:props.event._id}).toString()})}>
                                Shortlists
                            </ActionButton>
                            <ActionButton onClick={()=>navigate({pathname:"/admin/events/sde/planning",search:createSearchParams({eid:props.event._id}).toString()})}>
                                Planning
                            </ActionButton>
                        </>
                    ) : (
                        <>
                            <ActionButton primary onClick={()=>{
                                const isMilan2026 = props.event.title_fr?.toLowerCase().includes('milan 2026') ||
                                                   props.event.title_fr?.toLowerCase().includes('milan2026');

                                if (isMilan2026) {
                                    navigate({pathname:"/admin/events/fd100projects-modern",search:createSearchParams({eid:props.event._id}).toString()});
                                } else {
                                    navigate({pathname:"shortlists",search:createSearchParams({eid:props.event._id}).toString()});
                                }
                            }}>
                                Candidatures
                            </ActionButton>
                            {props.event.type !== "design" && (
                                <ActionButton onClick={()=>window.open("events/planning?eid="+props.event._id)}>
                                    Matching
                                </ActionButton>
                            )}
                        </>
                    )}
                </div>
            </EventLineContainer>
        )
    }

    // Past FD100 edition IDs (all editions stored in Supabase winners table)
    const PAST_EDITION_IDS = [
        'ec25d885-3dcf-455c-93b2-26041cb842e4', // 1ère édition
        '417b2020-721a-4c55-bd9f-d86a913f26ad', // 2ème édition
        'a3b7c9d1-e5f6-4a2b-8c3d-1e5f6a2b8c3d'  // 3ème édition (2023)
    ];

    function FDEventLine(props){

        function getEventStatus(event){
            if(new Date(event.inscriptions_start_date) > new Date()){
                return "À venir"
            }
            if(new Date(event.inscriptions_end_date).setHours(23, 59, 59) > new Date()){
                return "Inscriptions en cours"
            }
            return "Terminé"
        }

        const status = getEventStatus(props.event);
        const isActive = status === "Inscriptions en cours";
        const isPastEdition = PAST_EDITION_IDS.includes(props.event._id);

        return (
            <EventLineContainer isActive={isActive}>
                <div>
                    <strong>{props.event.title_fr}</strong>
                    <div className='thin'>Fin des inscriptions : {new dayjs(props.event.inscriptions_end_date).format('dddd D MMMM YYYY')}</div>
                    <EditButton onClick={()=>navigate({
                        pathname: isPastEdition ? "/admin/events/fd100-edit-supabase" : "/admin/events/fdeditevent",
                        search: createSearchParams({id:props.event._id}).toString()
                    })}>
                        Modifier <span className='crayon'>✎</span>
                    </EditButton>
                    {!isPastEdition && (
                        <PressButton onClick={()=>navigate({pathname:"/admin/events/fd100-press",search:createSearchParams({id:props.event._id}).toString()})}>
                            Page Presse <span>📰</span>
                        </PressButton>
                    )}
                    {isPastEdition ? (
                        <SuiviButton onClick={()=>navigate({pathname:"/admin/events/fd100-past-winners",search:createSearchParams({id:props.event._id}).toString()})}>
                            Suivi Lauréats <span>📊</span>
                        </SuiviButton>
                    ) : (
                        <SuiviButton onClick={()=>navigate({pathname:"/admin/events/fd100-suivi-laureats",search:createSearchParams({id:props.event._id}).toString()})}>
                            Suivi Lauréats <span>📊</span>
                        </SuiviButton>
                    )}
                    {isPastEdition && (
                        <SelectionFinaleButton onClick={()=>navigate({pathname:"/admin/events/fd100-past-edition-gallery",search:createSearchParams({id:props.event._id}).toString()})}>
                            100 Lauréats <span>🏆</span>
                        </SelectionFinaleButton>
                    )}
                    {props.event._id === '67590da5499bcc995c210b40' && (
                        <PartenairesButton onClick={()=>navigate({pathname:"/admin/events/fd100-partenaires",search:createSearchParams({id:props.event._id}).toString()})}>
                            Partenaires <span>🏭</span>
                        </PartenairesButton>
                    )}
                    {props.event._id === '67590da5499bcc995c210b40' && (
                        <SelectionFinaleButton onClick={()=>navigate({pathname:"/admin/events/fd100finalselection",search:createSearchParams({eid:props.event._id}).toString()})}>
                            Sélection Finale <span>🏆</span>
                        </SelectionFinaleButton>
                    )}
                    {props.event._id === '67590da5499bcc995c210b40' && (
                        <SuiviButton onClick={()=>navigate({pathname:"/admin/events/fd100-ceremony-rsvp"})}>
                            RSVP Cérémonie <span>📩</span>
                        </SuiviButton>
                    )}
                    {props.event._id === '67590da5499bcc995c210b40' && (
                        <SuiviButton onClick={()=>navigate({pathname:"/admin/events/fd100-talk-rsvp"})}>
                            RSVP Table Ronde <span>📩</span>
                        </SuiviButton>
                    )}
                    {props.event._id === '67590da5499bcc995c210b40' && (
                        <SuiviButton onClick={()=>navigate({pathname:"/admin/events/fd100-prix-du-public"})}>
                            Prix du Public <span>🗳️</span>
                        </SuiviButton>
                    )}
                    {props.event._id === '67590da5499bcc995c210b40' && (
                        <ExpoButton onClick={()=>navigate({pathname:"/admin/events/fd100-exposition"})}>
                            Expo <span>🏛️</span>
                        </ExpoButton>
                    )}
                </div>
                <StatusBadge status={status}>{status}</StatusBadge>

                <div className='button-column'>
                    {!isPastEdition && (
                        <>
                            <ActionButton primary onClick={()=>navigate({pathname:"/admin/events/fdadmin",search:createSearchParams({eid:props.event._id}).toString()})}>
                                Candidats
                            </ActionButton>
                            <ActionButton onClick={()=>navigate({pathname:"/admin/events/fdcompilationjury",search:createSearchParams({eid:props.event._id}).toString()})}>
                                Compilation jury
                            </ActionButton>
                        </>
                    )}
                </div>
            </EventLineContainer>
        )
    }

    return (
        <div>
            <PageHeader>
                <h1>Gestion des événements</h1>
            </PageHeader>

            {/* Add the new CurrentInscriptionsSection component here */}
            <CurrentInscriptionsSection events={allEvents} />

            <CreateEventButtonWrapper>
                <Link to="/admin/events/createevent-modern">
                    <CreateEventButton>+ Ajouter un événement</CreateEventButton>
                </Link>
            </CreateEventButtonWrapper>

            <SectionTitle icon="🎯">Speed Dating Events</SectionTitle>
            <EventsSection>
                {speedatings.length > 0 ?
                    <div>
                        {speedatings.map((sd, i) =>
                            <EventLine key={i} event={sd}/>
                        )}
                    </div>
                    :
                    <EmptyStateMessage>
                        Aucun Speed Dating pour le moment.
                    </EmptyStateMessage>
                }
            </EventsSection>

            <SectionTitle icon="🏆">Le French Design 100</SectionTitle>
            <EventsSection>
                {fd100s.length > 0 ?
                    <div>
                        <div style={{ textAlign: 'right', marginBottom: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <ActionButton primary onClick={() => navigate('/admin/events/fd100-all-editions')}>
                                Dashboard toutes editions
                            </ActionButton>
                            <ActionButton primary onClick={() => navigate('/admin/events/fd100-editions-export')}>
                                Export XLSX toutes éditions
                            </ActionButton>
                        </div>
                        {fd100s.map((fd, i) =>
                            <FDEventLine key={i} event={fd}/>
                        )}
                    </div>
                    :
                    <EmptyStateMessage>
                        Aucun FD 100 pour le moment.
                    </EmptyStateMessage>
                }
            </EventsSection>

            <SectionTitle icon="🎨">Design Events</SectionTitle>
            <EventsSection>
                {designEvents.length > 0 ?
                    <div>
                        {designEvents.map((de, i) =>
                            <EventLine key={i} event={de}/>
                        )}
                    </div>
                    :
                    <EmptyStateMessage>
                        Aucun événement de design pour le moment.
                    </EmptyStateMessage>
                }
            </EventsSection>


        </div>
    );
};

export default EventsAdminPanel;